import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import EventCard from "../components/events/EventCard";
import { Event } from "../types/events";
import dreamWorldProvider from "../DreamWorldProvider";
import { DocumentSnapshot } from "firebase/firestore";
import LoadingIndicator from "../components/ui/LoadingIndicator";
import PageLayout from "../components/ui/PageLayout";

type EventType = "live" | "coming-soon";

const AllEventsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { type } = useParams<{ type: EventType }>();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState<DocumentSnapshot | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const fetchEvents = async (initial = false) => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const { events: newEvents, lastVisible: newLastVisible } =
        await dreamWorldProvider.getEvents(
          initial ? undefined : lastVisible || undefined,
          10
        );

      const now = new Date();
      const filteredEvents = newEvents.filter((event) => {
        const eventDate = event.startDate;
        return type === "live" ? eventDate < now : eventDate >= now;
      });

      setEvents((prev) =>
        initial ? filteredEvents : [...prev, ...filteredEvents]
      );
      setLastVisible(newLastVisible);
      setHasMore(newEvents.length === 10);
    } catch (error) {
      console.error("Error fetching events: ", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents(true);
  }, [type]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fetchEvents();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loading, hasMore]);

  return (
    <div className="bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 min-h-screen text-white">
      <PageLayout>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
            <div>
              <h1 className="text-lg sm:text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {type === "live" ? "Live Events" : "Coming Soon"}
              </h1>
              <p className="text-gray-400 mt-2 text-sm sm:text-base">
                {type === "live"
                  ? "Events that are currently active."
                  : "Upcoming events to look forward to."}
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3  max-w-7xl mx-auto">
          {events.map((event, index) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, type: "spring" }}
            >
              <EventCard event={event} />
            </motion.div>
          ))}
        </div>

        <div
          ref={loadMoreRef}
          className="h-20 flex items-center justify-center"
        >
          {loading && <LoadingIndicator />}
          {!hasMore && <p>No more events to load.</p>}
        </div>
      </PageLayout>
    </div>
  );
};

export default AllEventsPage;
